import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Campo de formulario del sistema de diseño: etiqueta + control + ayuda/error.
 *
 * Completa lo que `Input` no puede saber por sí solo (el texto de la etiqueta y el mensaje
 * de validación) reutilizando su mismo estilo: el control sigue siendo el `Input` de
 * `src/components/ui/`, aquí solo se organizan las tres piezas.
 *
 * Convención de accesibilidad: `htmlFor` debe ser el `id` del control, y el mensaje de error
 * se pinta con `id="{htmlFor}-error"` para que la pantalla pueda enlazarlo:
 *   `aria-describedby={fieldError(errors, 'email') ? 'email-error' : undefined}`
 */

export interface FieldProps {
  /** Texto de la etiqueta. */
  label: string;
  /** `id` del control (se usa para el `htmlFor` y para el id del mensaje de error). */
  htmlFor: string;
  /** Marca el campo como obligatorio con un asterisco. */
  required?: boolean;
  /** Texto de ayuda (se oculta si hay error: el error tiene prioridad). */
  hint?: string;
  /** Mensaje de validación (del cliente o del backend). */
  error?: string;
  className?: string;
  children: ReactNode;
}

export function Field({
  label,
  htmlFor,
  required = false,
  hint,
  error,
  className,
  children,
}: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="block text-caption font-medium text-ink-soft">
        {label}
        {required ? (
          <span className="ml-0.5 text-danger" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {children}

      {error ? (
        <p id={`${htmlFor}-error`} className="text-micro text-danger-strong">
          {error}
        </p>
      ) : hint ? (
        <p className="text-micro text-ink-faint">{hint}</p>
      ) : null}
    </div>
  );
}
